import * as React from "react";

import { useMutation } from "react-query";
import { useDispatch } from "react-redux";

import { useTranslation } from "client/hooks/useTranslation";
import {
  logAmplitudeEvent,
  setAmplitudeUserId,
  setAmplitudeUserProperties,
} from "client/lib/amplitude";
import { addToGTMDataLayer } from "client/lib/gtm";
import * as AuthService from "client/services/auth";
import * as UserService from "client/services/user";
import { formCompletionActions, userActions } from "client/store";
import { getEmailFromUrlParams } from "client/utils/url";

type UseLoginProps = {
  onError: (errorString: string) => void;
  onCaptchaResult: (success: boolean) => void;
};

export function useLogin({ onError, onCaptchaResult }: UseLoginProps): any {
  const dispatch = useDispatch();
  const t = useTranslation();

  const [email, setEmail] = React.useState(getEmailFromUrlParams());

  React.useEffect(() => addToGTMDataLayer({ event: "OptInPageView" }), []);

  const { isLoading, mutate: login, ...rest } = useMutation(AuthService.login, {
    onSuccess: ({ userState, validation_errors, formCompletedState }) => {
      if (validation_errors) {
        onError(validation_errors?.lead?.email?.error as string);
        addToGTMDataLayer({
          event: "OptInButtonClick",
          existingEmail: "No",
          formSubmission: "Unsuccessful",
        });
      } else if (userState) {
        // update redux user state
        logAmplitudeEvent("REDESIGN_SIGN_UP", "clicked_submit_email");
        dispatch(userActions.setEmail(email));
        dispatch(userActions.setUserState(userState));
        if (formCompletedState) {
          dispatch(formCompletionActions.setFormCompletion(formCompletedState));
        }

        setAmplitudeUserProperties({
          isReturning: userState === "WAIT_FOR_EMAIL" ? "true" : "false",
        });

        addToGTMDataLayer({
          event: "OptInButtonClick",
          existingEmail: userState === "WAIT_FOR_EMAIL" ? "Yes" : "No",
          formSubmission: "Successful",
        });
        setAmplitudeUserId(email);
        localStorage.setItem("email", email.toLowerCase());
      }
    },
    onError: (e: Error) => {
      console.warn(e);
      // Q: surface server error to user or always display generic message?
      const errorMessage = e?.message ?? t("somethingWentWrong");
      onError(errorMessage);
    },
  });

  const { mutate: submitCaptcha } = useMutation(UserService.verifyCaptcha, {
    onSuccess: ({ success }) => {
      success
        ? logAmplitudeEvent("REDESIGN_SIGN_UP", "submitted_valid_captcha")
        : logAmplitudeEvent("REDESIGN_SIGN_UP", "submitted_invalid_captcha");
      onCaptchaResult(success ?? false);
    },
  });

  return {
    ...rest,
    email,
    isLoading,
    login,
    setEmail,
    submitCaptcha,
  };
}
